// Live derivation of a theme change's proposed/shipped status.
//
// The playground thesis treats a `theme` change as a *pending* thing: it's only
// "proposed" while it lives in the prototype theme alone; once it ships to prod
// (the llama theme) it's simply the theme, and the component goes back to
// pipe-through. The registry used to hand-store that status — which rots the
// moment a change merges (e.g. the pill Button radius is now in llama, yet the
// registry still said "proposed / not on prod / was 4px").
//
// Instead, derive it: diff each prototype override (designs/theme.tsx) against
// the llama base MUI theme. A leaf the base already matches has SHIPPED (the
// override is redundant); a leaf the base lacks or differs on is still
// PROPOSED. No status is stored, so it can't go stale.
//
// Scope: plain `defaultProps`, object `styleOverrides` and `variants`. A variant
// is matched by its `props` signature: one llama doesn't declare is part of the
// proposed change, the same as a token llama doesn't set. Function
// styleOverrides can't be evaluated statically; those are reported as
// `unresolved` and the panel falls back to the registry's authored note.
//
// Freshness caveat: the base theme imported here is the llama theme *on the
// current branch*. If the branch is behind origin/main, "shipped" means "in
// your checked-out llama". The session-start freshness check
// (scripts/ds-theme-freshness.mjs) flags when main has theme changes the branch
// hasn't pulled in, so the comparison can be trusted.

import { theme as llamaBaseTheme } from '@karehero/llama/theme/mui';

import { prototypeThemeOverrides } from '@/theme';

export type ThemeStatusDelta = {
  path: string; // dotted path within the component override, e.g. styleOverrides.root.borderRadius
  proto: string; // value the prototype proposes
  prod: string; // value on main/prod ('—' when prod doesn't set it)
};

export type DerivedThemeStatus = {
  status: 'proposed' | 'shipped';
  deltas: ThemeStatusDelta[]; // proto leaves that differ from prod → still proposed
  matched: string[]; // proto leaves prod already matches → shipped
  unresolved: boolean; // a function/variant override we couldn't compare statically
  note: string; // human-readable summary for the panel callout
};

const fmt = (v: unknown): string =>
  typeof v === 'string' ? v : JSON.stringify(v);

const eq = (a: unknown, b: unknown): boolean =>
  a === b || JSON.stringify(a) === JSON.stringify(b);

// Flatten an override subtree to primitive leaves keyed by dotted path.
// Functions can't be compared statically, so they flip `state.hadFn` instead of
// producing a leaf.
const collectLeaves = (
  value: unknown,
  prefix: string,
  out: Record<string, unknown>,
  state: { hadFn: boolean },
): void => {
  if (value === null || value === undefined) return;
  if (typeof value === 'function') {
    state.hadFn = true;
    return;
  }
  if (typeof value !== 'object') {
    out[prefix] = value;
    return;
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    collectLeaves(child, prefix ? `${prefix}.${key}` : key, out, state);
  }
};

const baseComponents = (llamaBaseTheme.components ?? {}) as Record<
  string,
  unknown
>;
const protoComponents = (prototypeThemeOverrides ?? {}) as Record<
  string,
  unknown
>;

// Variants are matched whole, by their `props` signature, rather than leaf by
// leaf: a variant is either declared in llama or it isn't.
const variantSignature = (v: unknown): string =>
  JSON.stringify((v as { props?: unknown })?.props ?? {});

const variantLabel = (v: unknown): string => {
  const props = (v as { props?: Record<string, unknown> })?.props ?? {};
  const parts = Object.entries(props).map(([k, val]) => `${k}='${val}'`);
  return parts.length ? parts.join(', ') : '(default)';
};

const variantsOf = (override: unknown): unknown[] => {
  const v = (override as { variants?: unknown })?.variants;
  return Array.isArray(v) ? v : [];
};

const withoutVariants = (override: unknown): unknown => {
  if (!override || typeof override !== 'object') return override;
  const { variants: _variants, ...rest } = override as Record<string, unknown>;
  return rest;
};

// Components hover-resolve under their display name, but a theme change may
// live on an internal MUI key (a TextField's variants live on MuiFilledInput).
// These map a display name to the extra MUI keys whose changes belong to it.
const MUI_KEY_ALIASES: Record<string, string[]> = {
  TextField: ['MuiFilledInput'],
  Select: ['MuiFilledInput'],
  Input: ['MuiFilledInput'],
  FilledInput: ['MuiFilledInput'],
};

// Derive status for a raw MUI component key (e.g. 'MuiButton'). Returns null
// when there's no prototype override for that component (nothing to derive) or
// when the only overrides are functions, which can't be compared statically.
export const deriveThemeStatusForMuiKey = (
  muiKey: string,
): DerivedThemeStatus | null => {
  const proto = protoComponents[muiKey];
  if (!proto) return null;

  const protoLeaves: Record<string, unknown> = {};
  const state = { hadFn: false };
  collectLeaves(withoutVariants(proto), '', protoLeaves, state);

  const prodLeaves: Record<string, unknown> = {};
  collectLeaves(withoutVariants(baseComponents[muiKey] ?? {}), '', prodLeaves, {
    hadFn: false,
  });

  const protoVariants = variantsOf(proto);
  const prodSigs = new Set(
    variantsOf(baseComponents[muiKey]).map(variantSignature),
  );

  const keys = Object.keys(protoLeaves);
  if (keys.length === 0 && protoVariants.length === 0) return null; // only functions

  const deltas: ThemeStatusDelta[] = [];
  const matched: string[] = [];
  for (const key of keys) {
    if (eq(protoLeaves[key], prodLeaves[key])) {
      matched.push(key);
    } else {
      deltas.push({
        path: key,
        proto: fmt(protoLeaves[key]),
        prod: key in prodLeaves ? fmt(prodLeaves[key]) : '—',
      });
    }
  }
  for (const v of protoVariants) {
    const label = variantLabel(v);
    if (prodSigs.has(variantSignature(v))) {
      matched.push(`variant ${label}`);
    } else {
      deltas.push({ path: 'variant', proto: label, prod: '—' });
    }
  }

  const status: DerivedThemeStatus['status'] =
    deltas.length > 0 ? 'proposed' : 'shipped';

  const note =
    status === 'shipped'
      ? `Already on main — the llama theme already sets ${matched
          .map((m) => (m in protoLeaves ? `${m} = ${fmt(protoLeaves[m])}` : m))
          .join(', ')}. The prototype override in designs/theme.tsx is now ` +
        `redundant: this is just the theme, so the component is back to pipe-through.`
      : `Prototype-only: ${deltas
          .map((d) =>
            d.path === 'variant'
              ? `a new variant, ${d.proto}`
              : `${d.path} = ${d.proto} (prod: ${d.prod})`,
          )
          .join(
            '; ',
          )}. Shipping it lands on every instance everywhere — only ` +
        `promote if that's the intent.`;

  return { status, deltas, matched, unresolved: state.hadFn, note };
};

// Map an inspected component's display name (e.g. 'Button') to its MUI keys and
// derive its theme-change status across them. Use baseName() on the inspection
// label first.
export const deriveThemeStatus = (
  displayName: string,
): DerivedThemeStatus | null => {
  const found = [`Mui${displayName}`, ...(MUI_KEY_ALIASES[displayName] ?? [])]
    .map(deriveThemeStatusForMuiKey)
    .filter((d): d is DerivedThemeStatus => d !== null);
  if (found.length <= 1) return found[0] ?? null;
  const proposed = found.filter((d) => d.status === 'proposed');
  return {
    status: proposed.length > 0 ? 'proposed' : 'shipped',
    deltas: found.flatMap((d) => d.deltas),
    matched: found.flatMap((d) => d.matched),
    unresolved: found.some((d) => d.unresolved),
    note: (proposed.length > 0 ? proposed : found).map((d) => d.note).join(' '),
  };
};

// A prototype override is REDUNDANT once its change has fully shipped to the
// llama theme: every leaf matches prod and nothing is left unresolved, so the
// override changes nothing and is safe to delete. The inspect-mode sweep deletes
// these from designs/theme.tsx — this lists them so the deletion is mechanical,
// not by eye. (A `shipped` status with `unresolved: true` is only partially
// matched — don't auto-delete; eyeball it.)
export const listRedundantOverrides = (): {
  muiKey: string;
  matched: string[];
}[] =>
  Object.keys(protoComponents)
    .map((muiKey) => ({ muiKey, derived: deriveThemeStatusForMuiKey(muiKey) }))
    .filter(
      ({ derived }) => derived?.status === 'shipped' && !derived.unresolved,
    )
    .map(({ muiKey, derived }) => ({
      muiKey,
      matched: derived!.matched,
    }));
